import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ThemeColors {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  accentColor?: string;
}

interface NavigationContextType {
  currentTheme: {
    type: 'site' | 'profile' | 'community';
    userId?: string;
    communityId?: string;
    colors?: ThemeColors;
  };
  setCurrentTheme: (theme: NavigationContextType['currentTheme']) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [currentTheme, setCurrentTheme] = useState<NavigationContextType['currentTheme']>(() => {
    // Initialize from sessionStorage on mount (for LoadingScreen continuity)
    try {
      const saved = sessionStorage.getItem('currentPageTheme');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
    } catch (error) {
      console.error('❌ [NavigationContext] Failed to restore theme:', error);
    }
    // Default to site theme
    return { type: 'site' };
  });

  const updateTheme = useCallback((theme: NavigationContextType['currentTheme']) => {
    setCurrentTheme(theme);

    // Persist to sessionStorage for LoadingScreen to read
    try {
      // setAt lets ThemeContext tell whether a site page mounted after this entry.
      sessionStorage.setItem('currentPageTheme', JSON.stringify({ ...theme, setAt: Date.now() }));
    } catch (error) {
      console.error('❌ [NavigationContext] Failed to save theme to cache:', error);
    }
  }, []);

  return (
    <NavigationContext.Provider value={{ currentTheme, setCurrentTheme: updateTheme }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};
